import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ApiThesaurusConcept,
    FormSelectOption,
    ITreeViewerNode,
    MultiFormSelectOptions,
    ShFieldDefinitionService,
    ThesaurusConceptReference,
    TreeViewerNode,
    apiThesaurusConceptToReference,
    isApiThesaurusConcept
} from '@stylehaven/components';
import { unionWith } from 'lodash-es';
import { ThesaurusService } from '../../../common/services/thesaurus/thesaurus.service';
import { ThesaurusTreeViewContainerComponent } from '../../containers/thesaurus-tree-view-container/thesaurus-tree-view-container.component';

@Component({
    selector: 'med-thesaurus-tree-multiple',
    templateUrl: './thesaurus-tree-multiple.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThesaurusTreeMultipleComponent extends ThesaurusTreeViewContainerComponent implements OnInit, OnDestroy {
    public tags$: BehaviorSubject<MultiFormSelectOptions<ThesaurusConceptReference>> = new BehaviorSubject<
        MultiFormSelectOptions<ThesaurusConceptReference>
    >({ options: [], isLiveData: false });

    @Input() set initialTags(initialTags: MultiFormSelectOptions<ThesaurusConceptReference>) {
        this.tags$.next(initialTags);
    }

    @Input() autoFocus: boolean = false;

    @Output() concepts: EventEmitter<MultiFormSelectOptions<ThesaurusConceptReference>> = new EventEmitter<
        MultiFormSelectOptions<ThesaurusConceptReference>
    >();

    constructor(
        protected thesaurusService: ThesaurusService,
        protected fieldDefinitionService: ShFieldDefinitionService,
        protected translateService: SharedLanguageService
    ) {
        super(thesaurusService, fieldDefinitionService, translateService);
    }

    protected initSuggestionLimit(): void {
        this.suggestionLimit$ = this.suggestions$.pipe(map(suggestions => false));
    }

    protected initEmissionStream(): void {
        this.tags$.pipe(takeUntil(this.destroy$)).subscribe(concepts => this.concepts.emit(concepts));
    }

    protected isActive(thesaurusConcept: ApiThesaurusConcept): boolean {
        return !!this.tags$.value.options.find(option => option.value.Uri === thesaurusConcept.Uri);
    }

    public onSelectedNodesChange(nodes: ITreeViewerNode<ApiThesaurusConcept>[]): void {
        const concepts: ApiThesaurusConcept[] = nodes.filter(node => !node.disabled).map(node => node.data);

        this.getFullBreadcrumbs(concepts)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(fullConcepts => {
                fullConcepts.forEach(concept => this.toggleConceptNodeState(concept, true));

                this.addTags(fullConcepts);
            });
    }

    public onDeselectNodes(nodes: ITreeViewerNode<ApiThesaurusConcept>[]): void {
        const removed: ApiThesaurusConcept[] = nodes.map(node => node.data);
        this.removeValues(removed);
    }

    public onUpdatedNodes(nodes: ITreeViewerNode<ApiThesaurusConcept>[]) {
        this.currentNodes = nodes;
    }

    public onLookupSelection(concept: ThesaurusConceptReference | null): void {
        if (!concept) {
            return;
        }

        this.addTags([concept]);

        const performedUpdate: boolean = this.toggleConceptNodeState(concept, true);

        if (performedUpdate) this.nodesUpdated$.next(true);
    }

    // returns true if an update happened
    protected toggleConceptNodeState(
        concept: ApiThesaurusConcept | ThesaurusConceptReference | null,
        state: boolean
    ): boolean {
        const conceptTreeNode: TreeViewerNode<ApiThesaurusConcept> | undefined = this.findTreeNodeById(
            this.currentNodes,
            concept?.Uri as string
        ) as TreeViewerNode<ApiThesaurusConcept> | undefined;

        if (!conceptTreeNode || conceptTreeNode.isActive === state) {
            return false;
        }

        conceptTreeNode.isActive = state;
        return true;
    }

    public addTags(concepts: (ApiThesaurusConcept | ThesaurusConceptReference)[]): void {
        if (!concepts || concepts.length === 0) {
            return;
        }

        const conceptOptions: FormSelectOption<ThesaurusConceptReference>[] =
            this.thesaurusApiConceptsToFormSelectOptions(concepts);
        const newValues: FormSelectOption<ThesaurusConceptReference>[] = unionWith(
            this.tags$.value.options,
            conceptOptions,
            (v1, v2) => v1.value.Uri === v2.value.Uri
        );

        if (newValues.length === this.tags$.value.options.length) return;

        this.tags$.next({
            options: newValues,
            isLiveData: this.tags$.value.isLiveData
        });
    }

    public removeValue(valueToRemove: ApiThesaurusConcept): void {
        this.removeValues([valueToRemove]);

        this.nodesUpdated$.next(true);
    }

    private removeValues(valuesToRemove: ApiThesaurusConcept[]): void {
        if (!valuesToRemove || valuesToRemove.length === 0) {
            return;
        }

        valuesToRemove.forEach(concept => this.toggleConceptNodeState(concept, false));

        const newValues: FormSelectOption<ThesaurusConceptReference>[] = this.tags$.value.options.filter(
            concept => !valuesToRemove.find(value => value.Uri === concept.value.Uri)
        );

        if (newValues.length === this.tags$.value.options.length) return;

        this.tags$.next({
            options: newValues,
            isLiveData: this.tags$.value.isLiveData
        });
    }

    private thesaurusApiConceptsToFormSelectOptions(
        concepts: (ApiThesaurusConcept | ThesaurusConceptReference)[]
    ): FormSelectOption<ThesaurusConceptReference>[] {
        const referenceConcepts: ThesaurusConceptReference[] = concepts.map(concept =>
            isApiThesaurusConcept(concept) ? apiThesaurusConceptToReference(concept) : concept
        );

        return referenceConcepts.map(concept => {
            return {
                label: this.thesaurusService.getThesaurusConceptReferenceLabel(concept),
                value: concept
            };
        });
    }
}
